import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchMissionLogs } from '../utils/api';
import LogSessionCard from '../components/LogsComponents/LogSessionCard';
import { logout } from '../utils/api';

const MissionLogs = () => {
    const navigate = useNavigate();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const getLogs = async () => {
            try {
                // Only show loading spinner on initial load, not background refreshes
                if (logs.length === 0) setLoading(true);

                const data = await fetchMissionLogs();

                // Transform API response to logs format
                const transformedLogs = [];

                if (data.success && Array.isArray(data.data)) {
                    // Sort duties by start_time descending (Newest first)
                    const sortedDuties = [...data.data].sort((a, b) =>
                        new Date(b.start_time) - new Date(a.start_time)
                    );

                    sortedDuties.forEach(duty => {
                        const collector = duty.collector || {};
                        const missions = duty.mission || [];

                        if (missions.length > 0) {
                            transformedLogs.push({
                                name: collector?.name || 'Unknown',
                                username: collector?.username || 'N/A',
                                employeeId: collector?.employee_id || 'N/A',
                                dsid: collector?.dsid || 'N/A',
                                rawDate: new Date(duty.start_time),
                                startedAt: formatDateTime(duty.start_time),
                                stoppedAt: duty.stop_time ? formatDateTime(duty.stop_time) : 'In Progress',
                                missions: missions
                            });
                        }
                    });
                }

                setLogs(transformedLogs);
                setError(null);
            } catch (err) {
                console.error("Error fetching logs:", err);
                if (logs.length === 0) {
                    setError("Failed to load request. Retrying...");
                }
            } finally {
                setLoading(false);
            }
        };
        // Initial fetch
        getLogs();
    }, []);

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        const d = date.getDate().toString().padStart(2, '0');
        const m = (date.getMonth() + 1).toString().padStart(2, '0');
        const y = date.getFullYear();
        const h = date.getHours().toString().padStart(2, '0');
        const min = date.getMinutes().toString().padStart(2, '0');
        const s = date.getSeconds().toString().padStart(2, '0');
        return `${d}/${m}/${y}, ${h}:${min}:${s}`;
    };

    return (
        <div className="min-h-[100dvh] bg-gray-50 flex flex-col font-sans">
            {/* Header Area */}
            <div className="px-6 pt-12 pb-6">
                <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Mission Logs</h1>
            </div>

            {/* Sign Out Button */}
            <button
                onClick={logout}
                className="absolute top-5 right-5 text-gray-500 hover:text-red-500 transition-colors"
                title="Sign Out"
            >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
            </button>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto px-6 pb-32">
                <div className="max-w-md mx-auto">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-20">
                            <div className="w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                            <p className="text-gray-500 font-medium">Loading logs...</p>
                        </div>
                    ) : error ? (
                        <div className="bg-red-50 border border-red-100 rounded-2xl p-6 text-center">
                            <p className="text-red-600 font-semibold">{error}</p>
                            <button
                                onClick={() => window.location.reload()}
                                className="mt-4 text-sm text-red-500 underline font-medium"
                            >
                                Try Refreshing
                            </button>
                        </div>
                    ) : logs.length > 0 ? (
                        logs.map((log, index) => (
                            <LogSessionCard key={index} log={log} />
                        ))
                    ) : (
                        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl border border-gray-100 shadow-sm">
                            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                                <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                </svg>
                            </div>
                            <p className="text-gray-500 font-bold text-lg">No Logs Found</p>
                            <p className="text-gray-400 text-sm mt-1">Your missions will appear here once submitted.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Bottom Navigation */}
            <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 z-50 shadow-[0_-10px_30px_rgba(0,0,0,0.02)]">
                <div className="flex items-center justify-center gap-x-16 py-4 max-w-md mx-auto">
                    {/* Home */}
                    <button
                        onClick={() => navigate('/home')}
                        className="flex flex-col items-center space-y-1 text-gray-400 group"
                    >
                        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                        <span className="text-[10px] font-bold uppercase tracking-tight">Home</span>
                    </button>

                    {/* Logs - Active */}
                    <button
                        onClick={() => navigate('/logs')}
                        className="flex flex-col items-center space-y-1 text-teal-600 group"
                    >
                        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-[10px] font-bold uppercase tracking-tight">Logs</span>
                        <div className="w-10 h-[3px] bg-teal-500 rounded-full mt-0.5"></div>
                    </button>
                </div>
            </nav>
        </div>
    );
};

export default MissionLogs;